import { CALL_API } from 'store/middleware/api';
import { setCode } from 'store/middleware/api/activate';
import { isProduction } from 'utils/brand';
import apiService from './apiService';
import { billingDashboardMock, defaultPaymentSettingList, getBillingList, teamRes, walletListMock } from './constants';
import { joinPath } from 'utils/string';

export const AUTHENTICATE = ('AUTHENTICATE');
export const AUTHENTICATION_SUCCESS = ('AUTHENTICATION_SUCCESS');
export const AUTHENTICATION_ERROR = ('AUTHENTICATION_ERROR');
export const LOAD_USER_PROFILE_SUCCESS = ('LOAD_USER_PROFILE_SUCCESS');
export const LOAD_USER_PROFILE_ERROR = ('LOAD_USER_PROFILE_ERROR');
export const LOAD_MARKETING_PROFILE_SUCCESS = ('LOAD_MARKETING_PROFILE_SUCCESS');
export const LOAD_MARKETING_PROFILE_ERROR = ('LOAD_MARKETING_PROFILE_ERROR');
export const LOAD_LEAD_AUTOMATOR_SUCCESS = ('LOAD_LEAD_AUTOMATOR_SUCCESS');
export const IS_LEAD_AUTOMATOR_LOADING = ('IS_LEAD_AUTOMATOR_LOADING');
export const LOAD_TEAM_MEMBERS_SUCCESS = ('LOAD_TEAM_MEMBERS_SUCCESS');
export const LOAD_TEAM_MEMBERS_ERROR = ('LOAD_TEAM_MEMBERS_ERROR');
export const SET_SELECTED_TEAM_MEMBER = ('SET_SELECTED_TEAM_MEMBER');
export const LOAD_PREPAID_CREDITS_SUCCESS = ('LOAD_PREPAID_CREDITS_SUCCESS');
export const LOAD_PLAN_SUCCESS = ('LOAD_PLAN_SUCCESS');
export const LOAD_PLAN_ERROR = ('LOAD_PLAN_ERROR');
export const LOAD_LA_PLAN_SUCCESS = ('LOAD_LA_PLAN_SUCCESS');
export const LOAD_LA_PLAN_ERROR = ('LOAD_LA_PLAN_ERROR');

export function authenticate(token, activationCode, overtake, redirectMessage, afterSuccess, afterError) {
  const result = {};
  result[CALL_API] = {
    method: 'get',
    path: '/resource/auth',
    query: {
      t: token,
      c: activationCode,
      overtake,
      redirectMessage,
      dev: !isProduction(),
    },
    startType: AUTHENTICATE,
    successType: AUTHENTICATION_SUCCESS,
    errorType: AUTHENTICATION_ERROR,
    afterSuccess: (res) => {
      setCode(res.response.activationCode);
      sessionStorage.setItem('isLoggedIn', JSON.stringify(true))
      sessionStorage.removeItem('logOutClicked')
      if (afterSuccess) afterSuccess(res);
    },
    afterError,
  };

  return result;
}

export const LOGOUT = ('LOGOUT');
export const LOGOUT_SUCCESS = ('LOGOUT_SUCCESS');
export const LOGOUT_ERROR = ('LOGOUT_ERROR');

// Other tabs listen for changes to this key (see SessionOverlay) so a logout in one tab logs out every tab.
export const LOGOUT_BROADCAST_KEY = 'ps.logout';

// Broadcast only once the logout call has returned, so the token is already revoked when other tabs reach the login page.
const broadcastLogout = () => {
  try {
    localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now()));
  } catch (e) {
    // localStorage unavailable (e.g. blocked site data); other tabs are still logged out on their next API call.
  }
};

export function logout(mode) {
  const result = {};
  result[CALL_API] = {
    method: 'get',
    path: '/resource/auth/logout',
    query: { mode },
    startType: LOGOUT,
    successType: LOGOUT_SUCCESS,
    errorType: LOGOUT_ERROR,
    // The logout endpoint responds 403, so the error path is the normal one.
    afterSuccess: broadcastLogout,
    afterError: broadcastLogout,
  };

  return result;
}

export const SET_LOGOUT = ('SET_LOGOUT');
export const setLogout = message => ({ type: SET_LOGOUT, message });

export const SET_CHAT_VISIBLE = ('SET_CHAT_VISIBLE');
export const setChatVisible = visible => ({ type: SET_CHAT_VISIBLE, visible });

export const EXTEND_SESSION = ('EXTEND_SESSION');
export const EXTEND_SESSION_SUCCESS = ('EXTEND_SESSION_SUCCESS');
export const EXTEND_SESSION_ERROR = ('EXTEND_SESSION_ERROR');

export function extendSession() {
  const result = {};
  result[CALL_API] = {
    method: 'get',
    path: '/resource/auth/extend',
    startType: EXTEND_SESSION,
    successType: EXTEND_SESSION_SUCCESS,
    errorType: EXTEND_SESSION_ERROR,
  };

  return result;
}

export const SAVE_PRELIMINARY_DATA = ('SAVE_PRELIMINARY_DATA');
export const SAVE_PRELIMINARY_DATA_SUCCESS = ('SAVE_PRELIMINARY_DATA_SUCCESS');
export const SAVE_PRELIMINARY_DATA_ERROR = ('SAVE_PRELIMINARY_DATA_ERROR');

export function savePreliminaryData(body, afterError) {
  const result = {};
  result[CALL_API] = {
    method: 'post',
    path: '/resource/auth/pre',
    body,
    afterError,
    startType: SAVE_PRELIMINARY_DATA,
    successType: SAVE_PRELIMINARY_DATA_SUCCESS,
    errorType: SAVE_PRELIMINARY_DATA_ERROR,
  };

  return result;
}

export const SAVE_PROFILE = ('SAVE_PROFILE');
export const SAVE_PROFILE_SUCCESS = ('SAVE_PROFILE_SUCCESS');
export const SAVE_PROFILE_ERROR = ('SAVE_PROFILE_ERROR');

export function saveProfile(body) {
  const result = {};
  result[CALL_API] = {
    caseTransform: true,
    method: 'post',
    body,
    path: '/resource/auth/ps4/user/profile',
    startType: SAVE_PROFILE,
    successType: SAVE_PROFILE_SUCCESS,
    errorType: SAVE_PROFILE_ERROR,
  };

  return result;
}

export const DISABLE_ALERT = ('DISABLE_ALERT');
export const DISABLE_ALERT_SUCCESS = ('DISABLE_ALERT_SUCCESS');
export const DISABLE_ALERT_ERROR = ('DISABLE_ALERT_ERROR');

export function disableAlert(id, afterSuccess) {
  const result = {};
  result[CALL_API] = {
    caseTransform: true,
    method: 'post',
    path: `/resource/auth/ps4/user/alerts/${String(id)}`,
    startType: DISABLE_ALERT,
    successType: DISABLE_ALERT_SUCCESS,
    errorType: DISABLE_ALERT_ERROR,
    afterSuccess,
  };

  return result;
}

export const LOAD_ALERT = ('LOAD_ALERT');
export const LOAD_ALERT_SUCCESS = ('LOAD_ALERT_SUCCESS');
export const LOAD_ALERT_ERROR = ('LOAD_ALERT_ERROR');

export function getAlertByName(alertName, afterSuccess) {
  const result = {};
  result[CALL_API] = {
    caseTransform: true,
    method: 'get',
     path: `/resource/auth/ps4/user/alert/by_name/${encodeURIComponent(alertName)}`,
    startType: LOAD_ALERT,
    successType: LOAD_ALERT_SUCCESS,
    errorType: LOAD_ALERT_ERROR,
    afterSuccess,
  };

  return result;
}

export const UPGRADE_ACCOUNT = ('UPGRADE_ACCOUNT');
export const UPGRADE_ACCOUNT_SUCCESS = ('UPGRADE_ACCOUNT_SUCCESS');
export const UPGRADE_ACCOUNT_ERROR = ('UPGRADE_ACCOUNTERROR');

export function upgradeAccount(user, catalogProductId, planMrcId, quantity, approvedAmount, accountPassword, afterSuccess) {
  const result = {};
  result[CALL_API] = {
    method: 'post',
    body: user,
    query: { catalogProductId, planMrcId, quantity, approvedAmount, accountPassword },
    path: '/resource/auth/ps4/user/upgrade',
    startType: UPGRADE_ACCOUNT,
    successType: UPGRADE_ACCOUNT_SUCCESS,
    errorType: UPGRADE_ACCOUNT_ERROR,
    afterSuccess,
  };

  return result;
}

export const REMOVE_ERROR = ('REMOVE_ERROR');

export function removeError() {
  return {
    type: REMOVE_ERROR,
  };
}

export const SAVE_PREPAID_CREDIT = ('SAVE_PREPAID_CREDIT');
export const SAVE_PREPAID_CREDIT_SUCCESS = ('SAVE_PREPAID_CREDIT_SUCCESS');
export const SAVE_PREPAID_CREDIT_ERROR = ('SAVE_PREPAID_CREDIT_ERROR');
export const GET_PREPAID_CREDIT = ('GET_PREPAID_CREDIT');
export const GET_PREPAID_CREDIT_SUCCESS = ('GET_PREPAID_CREDIT_SUCCESS');
export const GET_PREPAID_CREDIT_ERROR = ('GET_PREPAID_CREDIT_ERROR');
export const LOAD_OFFERS = ('LOAD_OFFERS');
export const LOAD_OFFERS_SUCCESS = ('LOAD_OFFERS_SUCCESS');
export const LOAD_OFFERS_ERROR = ('LOAD_OFFERS_ERROR');
export const SAVE_OFFER = ('SAVE_OFFER');
export const SAVE_OFFER_SUCCESS = ('SAVE_OFFER_SUCCESS');
export const SAVE_OFFER_ERROR = ('SAVE_OFFER_ERROR');
export const DELETE_OFFER = ('DELETE_OFFER');
export const DELETE_OFFER_SUCCESS = ('DELETE_OFFER_SUCCESS');
export const DELETE_OFFER_ERROR = ('DELETE_OFFER_ERROR');
export const LOAD_OFFER_REPORT = ('LOAD_OFFER_REPORT');
export const LOAD_OFFER_REPORT_SUCCESS = ('LOAD_OFFER_REPORT_SUCCESS');
export const LOAD_OFFER_REPORT_ERROR = ('LOAD_OFFER_REPORT_ERROR');
export const GET_USERS = ('GET_USERS');
export const GET_USERS_SUCCESS = ('GET_USERS_SUCCESS');
export const GET_USERS_ERROR = ('GET_USERS_ERROR');
export const SAVE_USER = ('SAVE_USER');
export const SAVE_USER_SUCCESS = ('SAVE_USER_SUCCESS');
export const SAVE_USER_ERROR = ('SAVE_USER_ERROR');
export const DELETE_USER = ('DELETE_USER');
export const DELETE_USER_SUCCESS = ('DELETE_USER_SUCCESS');
export const DELETE_USER_ERROR = ('DELETE_USER_ERROR');
export const GET_TRANSACTIONS = ('GET_TRANSACTIONS');
export const GET_TRANSACTIONS_SUCCESS = ('GET_TRANSACTIONS_SUCCESS');
export const GET_TRANSACTIONS_ERROR = ('GET_TRANSACTIONS_ERROR');
export const SAVE_LAYOUT = ('SAVE_LAYOUT');
export const SAVE_LAYOUT_SUCCESS = ('SAVE_LAYOUT_SUCCESS');
export const SAVE_LAYOUT_ERROR = ('SAVE_LAYOUT_ERROR');
export const DELETE_LAYOUT = ('DELETE_LAYOUT');
export const DELETE_LAYOUT_SUCCESS = ('DELETE_LAYOUT_SUCCESS');
export const DELETE_LAYOUT_ERROR = ('DELETE_LAYOUT_ERROR');
export const SAVE_TAG = ('SAVE_TAG');
export const SAVE_TAG_SUCCESS = ('SAVE_TAG_SUCCESS');
export const SAVE_TAG_ERROR = ('SAVE_TAG_ERROR');
export const DELETE_TAG = ('DELETE_TAG');
export const DELETE_TAG_SUCCESS = ('DELETE_TAG_SUCCESS');
export const DELETE_TAG_ERROR = ('DELETE_TAG_ERROR');
export const GET_HS_IDENTIFICATION_TOKEN = ('GET_HS_IDENTIFICATION_TOKEN');
export const GET_HS_IDENTIFICATION_TOKEN_SUCCESS = ('GET_HS_IDENTIFICATION_TOKEN_SUCCESS');
export const GET_HS_IDENTIFICATION_TOKEN_ERROR = ('GET_HS_IDENTIFICATION_TOKEN_ERROR');



// Billing
// Actions Types

export const LOAD_BILLING_DASHBOARD_SUCCESS = ('LOAD_BILLING_DASHBOARD_SUCCESS');
export const LOAD_BILLING_DASHBOARD_ERROR = ('LOAD_BILLING_DASHBOARD_ERROR');

export const LOAD_PAYMENT_LIST = ('LOAD_PAYMENT_LIST');
export const LOAD_PAYMENT_LIST_SUCCESS = ('LOAD_PAYMENT_LIST_SUCCESS');
export const LOAD_PAYMENT_LIST_ERROR = ('LOAD_PAYMENT_LIST_ERROR');

export const ADD_PAYMENT_METHOD = ('ADD_PAYMENT_METHOD');
export const ADD_PAYMENT_METHOD_SUCCESS = ('ADD_PAYMENT_METHOD_SUCCESS');
export const ADD_PAYMENT_METHOD_ERROR = ('ADD_PAYMENT_METHOD_ERROR');

export const UPDATE_PAYMENT_METHOD = ('UPDATE_PAYMENT_METHOD');
export const UPDATE_PAYMENT_METHOD_SUCCESS = ('UPDATE_PAYMENT_METHOD_SUCCESS');
export const UPDATE_PAYMENT_METHOD_ERROR = ('UPDATE_PAYMENT_METHOD_ERROR');

export const DELETE_PAYMENT_METHOD = ('DELETE_PAYMENT_METHOD');
export const DELETE_PAYMENT_METHOD_SUCCESS = ('DELETE_PAYMENT_METHOD_SUCCESS');
export const DELETE_PAYMENT_METHOD_ERROR = ('DELETE_PAYMENT_METHOD_ERROR');



export const LOAD_DEFAULT_PAYMENT_SETTING = ('LOAD_DEFAULT_PAYMENT_SETTING');
export const LOAD_DEFAULT_PAYMENT_SETTING_SUCCESS = ('LOAD_DEFAULT_PAYMENT_SETTING_SUCCESS');
export const LOAD_DEFAULT_PAYMENT_SETTING_ERROR = ('LOAD_DEFAULT_PAYMENT_SETTING_ERROR');


export const LOAD_WALLET_HISTORY_LIST = ('LOAD_WALLET_HISTORY_LIST');
export const LOAD_WALLET_HISTORY_LIST_SUCCESS = ('LOAD_WALLET_HISTORY_LIST_SUCCESS');
export const LOAD_WALLET_HISTORY_LIST_ERROR = ('LOAD_WALLET_HISTORY_LIST_ERROR');
export const UPDATE_PAYMENT_PROFILE = ('UPDATE_PAYMENT_PROFILE');
export const LOAD_USAGE_SUCCESS = ('LOAD_USAGE_SUCCESS');
export const LOAD_USAGE_ERROR = ('LOAD_USAGE_ERROR');
export const UPDATE_SELECTED_LEAD_AUTOMATOR = ('UPDATE_SELECTED_LEAD_AUTOMATOR');
export const UPDATE_TRIAL_14_LEAD_AUTOMATOR = ('UPDATE_TRIAL_14_LEAD_AUTOMATOR');
export const UPDATE_SELECTED_MANAGE_PLAN = ('UPDATE_SELECTED_MANAGE_PLAN');
export const UPDATE_SELECTED_CHOOSE_TIER_TOOLTIP = ('UPDATE_SELECTED_CHOOSE_TIER_TOOLTIP');
export const UPDATE_PLAN_MONTHLY_ANNUAL = ('UPDATE_PLAN_MONTHLY_ANNUAL');

export const LOAD_PLAN_TYPE_SUCCESS = ('LOAD_PLAN_TYPE_SUCCESS');
export const LOAD_PLAN_TYPE_ERROR = ('LOAD_PLAN_TYPE_ERROR');

export const LOAD_ACCOUNT_TYPE_SUCCESS = ('LOAD_ACCOUNT_TYPE_SUCCESS');
export const LOAD_TEAM_MEMBERS_REQUEST = ('LOAD_TEAM_MEMBERS_REQUEST');

export const LOAD_BATCH_DIALER_INTEGRATION_SUCCESS = 'LOAD_BATCH_DIALER_INTEGRATION_SUCCESS';
export const LOAD_BATCH_DIALER_INTEGRATION_ERROR = 'LOAD_BATCH_DIALER_INTEGRATION_ERROR';

export const LOAD_CAMPAIGNS_INTEGRATION_SUCCESS = 'LOAD_CAMPAIGNS_INTEGRATION_SUCCESS';
export const LOAD_CAMPAIGNS_INTEGRATION_ERROR = 'LOAD_CAMPAIGNS_INTEGRATION_ERROR';

export const LOAD_STATE_AREA_INTEGRATION_SUCCESS = 'LOAD_STATE_AREA_INTEGRATION_SUCCESS';
export const LOAD_STATE_AREA_INTEGRATION_ERROR = 'LOAD_STATE_AREA_INTEGRATION_ERROR';


export const SET_BATCH_DIALER_SOURCE = 'SET_BATCH_DIALER_SOURCE';
export const LOAD_CALL_SETTING_INTEGRATION = 'LOAD_CALL_SETTING_INTEGRATION';
export const LOAD_CALL_SETTING_INTEGRATION_SUCCESS = 'LOAD_CALL_SETTING_INTEGRATION_SUCCESS';
export const LOAD_CALL_SETTING_INTEGRATION_ERROR = 'LOAD_CALL_SETTING_INTEGRATION_ERROR';
export const DELETE_CALL_SETTING_SUCCESS = 'DELETE_CALL_SETTING_SUCCESS';
export const DELETE_CALL_SETTING_ERROR = 'DELETE_CALL_SETTING_ERROR';
export const LOAD_CALL_SETTINGS_STATE_AREA_CODES_SUCCESS = 'LOAD_CALL_SETTINGS_STATE_AREA_CODES_SUCCESS';
export const LOAD_CALL_SETTINGS_STATE_AREA_CODES_ERROR = 'LOAD_CALL_SETTINGS_STATE_AREA_CODES_ERROR';
export const LOADING_CALL_SETTINGS_AVAILABLE_OUTBOUND_NUMBERS = 'LOADING_CALL_SETTINGS_AVAILABLE_OUTBOUND_NUMBERS';
export const LOAD_CALL_SETTINGS_AVAILABLE_OUTBOUND_NUMBERS_SUCCESS = 'LOAD_CALL_SETTINGS_AVAILABLE_OUTBOUND_NUMBERS_SUCCESS';
export const LOAD_CALL_SETTINGS_AVAILABLE_OUTBOUND_NUMBERS_ERROR = 'LOAD_CALL_SETTINGS_AVAILABLE_OUTBOUND_NUMBERS_ERROR';

// Click to Dial
export const OPEN_CLICK_TO_DIAL = 'OPEN_CLICK_TO_DIAL';
export const CLOSE_CLICK_TO_DIAL = 'CLOSE_CLICK_TO_DIAL';
export const IS_CLICK_TO_DIAL_LOADED = 'IS_CLICK_TO_DIAL_LOADED';
export const LOAD_CLICK_TO_DIAL_CONTACT_PROPERTY_DETAILS_SUCCESS = 'LOAD_CLICK_TO_DIAL_CONTACT_PROPERTY_DETAILS_SUCCESS';
export const LOAD_CLICK_TO_DIAL_CONTACT_PROPERTY_DETAILS_ERROR = 'LOAD_CLICK_TO_DIAL_CONTACT_PROPERTY_DETAILS_ERROR';
export const SET_CALL_COMPLIANCE = "SET_CALL_COMPLIANCE";

//UPGRADE_TO_LEAD_AUTOMATOR

export const OPEN_LEAD_AUTOMATOR_UPGRADE = 'OPEN_LEAD_AUTOMATOR_UPGRADE';

export const CHECK_CONTACTS_PROPERTY_ASSOCIATION =
  'CHECK_CONTACTS_PROPERTY_ASSOCIATION';
export const CHECK_CONTACTS_PROPERTY_ASSOCIATION_SUCCESS =
  'CHECK_CONTACTS_PROPERTY_ASSOCIATION_SUCCESS';
export const CHECK_CONTACTS_PROPERTY_ASSOCIATION_ERROR =
  'CHECK_CONTACTS_PROPERTY_ASSOCIATION_ERROR';

export const UPDATE_DIALER_CAMPAIGN = 'UPDATE_DIALER_CAMPAIGN';
export const UPDATE_DIALER_CAMPAIGN_SUCCESS = 'UPDATE_DIALER_CAMPAIGN_SUCCESS';
export const UPDATE_DIALER_CAMPAIGN_ERROR = 'UPDATE_DIALER_CAMPAIGN_ERROR';

export const DUPLICATE_DIALER_CAMPAIGN = 'DUPLICATE_DIALER_CAMPAIGN';
export const DUPLICATE_DIALER_CAMPAIGN_SUCCESS = 'DUPLICATE_DIALER_CAMPAIGN_SUCCESS';
export const DUPLICATE_DIALER_CAMPAIGN_ERROR = 'DUPLICATE_DIALER_CAMPAIGN_ERROR';

export const CREATE_DIALER_CAMPAIGN = 'CREATE_DIALER_CAMPAIGN';
export const CREATE_DIALER_CAMPAIGN_SUCCESS = 'CREATE_DIALER_CAMPAIGN_SUCCESS';
export const CREATE_DIALER_CAMPAIGN_ERROR = 'CREATE_DIALER_CAMPAIGN_ERROR';

export const LOAD_CALL_COUNT_FOR_TODAY = 'LOAD_CALL_COUNT_FOR_TODAY';
export const LOAD_CALL_COUNT_FOR_TODAY_SUCCESS = 'LOAD_CALL_COUNT_FOR_TODAY_SUCCESS';
export const LOAD_CALL_COUNT_FOR_TODAY_ERROR = 'LOAD_CALL_COUNT_FOR_TODAY_ERROR';

function request(path, method, startType, successType, errorType, body, { afterSuccess, afterError, params, successParams, download = false, query } = {}) {
  return {
    [CALL_API]: {
      method,
      startType,
      successType,
      errorType,
      body,
      afterSuccess,
      afterError,
      params,
      successParams,
      query,
      download,
      path: joinPath('/resource/auth/ps4', path),
      // ...(method === 'post' && !isFormData(body) ? { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } } : {}),
    },
  };
}


const user = (path, method, startType, successType, errorType, body, params) => request(joinPath('user', path), method, startType, successType, errorType, body, params);
const offer = (path, method, startType, successType, errorType, body, params) => user(joinPath('offers', path), method, startType, successType, errorType, body, params);


export const getUsers = () => user('team/members', 'get', GET_USERS, GET_USERS_SUCCESS, GET_USERS_ERROR);

export const saveUser = (data, afterSuccess) => user('team/members', 'put', SAVE_USER, SAVE_USER_SUCCESS, SAVE_USER_ERROR, data, { afterSuccess });

export const deleteUser = (id, afterSuccess) => user(`team/members/${id}`, 'delete', DELETE_USER, DELETE_USER_SUCCESS, DELETE_USER_ERROR, null, { afterSuccess });

export const savePrepaidCredit = (paymentMethodId, amount, cardCode, afterSuccess) => user('credits', 'post', SAVE_PREPAID_CREDIT, SAVE_PREPAID_CREDIT_SUCCESS, SAVE_PREPAID_CREDIT_ERROR, null, { query: { paymentMethodId, amount, cardCode }, afterSuccess });

export const getPrepaidCredit = afterSuccess => user('credits', 'get', GET_PREPAID_CREDIT, GET_PREPAID_CREDIT_SUCCESS, GET_PREPAID_CREDIT_ERROR, null, { afterSuccess });

export const getTransactions = () => user('transactions', 'get', GET_TRANSACTIONS, GET_TRANSACTIONS_SUCCESS, GET_TRANSACTIONS_ERROR);

export const loadOffers = savedPropertyId => offer(`property/${savedPropertyId}`, 'get', LOAD_OFFERS, LOAD_OFFERS_SUCCESS, LOAD_OFFERS_ERROR);

export const saveOffer = (offerAnalysis, afterSuccess) => offer('', 'put', SAVE_OFFER, SAVE_OFFER_SUCCESS, SAVE_OFFER_ERROR, offerAnalysis, { afterSuccess });

export const deleteOffer = (id, afterSuccess) => offer(id, 'delete', DELETE_OFFER, DELETE_OFFER_SUCCESS, DELETE_OFFER_ERROR, null, { afterSuccess });

export const loadOfferReport = (offerAnalysis, reportCode) => offer(`reports/${reportCode}`, 'post', LOAD_OFFER_REPORT, LOAD_OFFER_REPORT_SUCCESS, LOAD_OFFER_REPORT_ERROR, offerAnalysis);

export const loadSavedOfferReport = (id, reportCode) => offer(`${id}/reports/${reportCode}`, 'get', LOAD_OFFER_REPORT, LOAD_OFFER_REPORT_SUCCESS, LOAD_OFFER_REPORT_ERROR);

export const saveLayout = (layout, afterSuccess) => user('layouts', 'put', SAVE_LAYOUT, SAVE_LAYOUT_SUCCESS, SAVE_LAYOUT_ERROR, layout, { afterSuccess });

export const deleteLayout = (id, afterSuccess) => user(`layouts/${id}`, 'delete', DELETE_LAYOUT, DELETE_LAYOUT_SUCCESS, DELETE_LAYOUT_ERROR, null, { afterSuccess });

export const saveTag = (tag, afterSuccess) => user('tags', 'put', SAVE_TAG, SAVE_TAG_SUCCESS, SAVE_TAG_ERROR, tag, { afterSuccess });

export const deleteTag = id => user(`tags/${id}`, 'delete', DELETE_TAG, DELETE_TAG_SUCCESS, DELETE_TAG_ERROR);

export const getHsIdentificationToken = (afterSuccess, afterError) => user('hsidtoken', 'get', GET_HS_IDENTIFICATION_TOKEN, GET_HS_IDENTIFICATION_TOKEN_SUCCESS, GET_HS_IDENTIFICATION_TOKEN_ERROR, null, { afterSuccess, afterError });

// Marketing Profile
export const loadMarketingProfileSuccess = (payload) => ({
  type: LOAD_MARKETING_PROFILE_SUCCESS,
  payload,
});

export const loadMarketingProfileError = (payload) => ({
  type: LOAD_MARKETING_PROFILE_ERROR,
  payload,
});

export const loadUserProfileSuccess = (payload) => ({
  type: LOAD_USER_PROFILE_SUCCESS,
  payload,
});

export const loadUserProfileError = (payload) => ({
  type: LOAD_USER_PROFILE_ERROR,
  payload,
});

export const loadPrepaidCreditsSuccess = (payload) => ({
  type: LOAD_PREPAID_CREDITS_SUCCESS,
  payload,
});

export const loadPlanSuccess = (payload) => ({
  type: LOAD_PLAN_SUCCESS,
  payload,
});

export const loadPlanFailure = (payload) => ({
  type: LOAD_PLAN_ERROR,
  payload,
});

export const loadLAPlanSuccess = (payload) => ({
  type: LOAD_LA_PLAN_SUCCESS,
  payload,
});

export const loadLAPlanFailure = (payload) => ({
  type: LOAD_LA_PLAN_ERROR,
  payload,
});

export const loadLeadAutomatorSuccess = (payload) => ({
  type: LOAD_LEAD_AUTOMATOR_SUCCESS,
  payload,
});

export const loadTeamMembersRequest = () => ({
  type: LOAD_TEAM_MEMBERS_REQUEST,
});

export const loadTeamMembersSuccess = (payload) => ({
  type: LOAD_TEAM_MEMBERS_SUCCESS,
  payload,
});

export const loadTeamMembersFailure = (payload) => ({
  type: LOAD_TEAM_MEMBERS_ERROR,
  payload,
});


export const selectedTeamMember = (payload) => ({
  type: SET_SELECTED_TEAM_MEMBER,
  payload,
});

// Billing
export const loadBillingDashboardSuccess = (payload) => ({
  type: LOAD_BILLING_DASHBOARD_SUCCESS,
  payload,
});

export const loadBillingDashboardError = (payload) => ({
  type: LOAD_BILLING_DASHBOARD_ERROR,
  payload,
});

export const loadWalletSuccess = (payload) => ({
  type: LOAD_PAYMENT_LIST_SUCCESS,
  payload,
});

export const loadWalletError = (payload) => ({
  type: LOAD_PAYMENT_LIST_ERROR,
  payload,
});

export const loadWalletDefaultSettingSuccess = (payload) => ({
  type: LOAD_DEFAULT_PAYMENT_SETTING_SUCCESS,
  payload,
});

export const loadWalletDefaultSettingError = (payload) => ({
  type: LOAD_DEFAULT_PAYMENT_SETTING_ERROR,
  payload,
});

export const loadBillingHistorySuccess = (payload) => ({
  type: LOAD_WALLET_HISTORY_LIST_SUCCESS,
  payload,
});

export const loadBillingHistoryError = (payload) => ({
  type: LOAD_WALLET_HISTORY_LIST_ERROR,
  payload,
});

// Usage section

export const loadUsageSuccess = (payload) => ({
  type: LOAD_USAGE_SUCCESS,
  payload, // The data fetched from the API
});

export const loadUsageError = (payload) => ({
  type: LOAD_USAGE_ERROR,
  payload,
});

// Plan list

export const loadPlanTypeSuccess = (payload) => ({
  type: LOAD_PLAN_TYPE_SUCCESS,
  payload,
});

export const loadPlanTypeError = (payload) => ({
  type: LOAD_PLAN_TYPE_ERROR,
  payload,
});

export const loadAccountType = (payload) => ({
  type: LOAD_ACCOUNT_TYPE_SUCCESS,
  payload,
});

// Batchdialer
export const loadBatchDialerIntegrationSuccess = (payload) => ({
  type: LOAD_BATCH_DIALER_INTEGRATION_SUCCESS,
  payload,
});

export const loadBatchDialerIntegrationError = (payload) => ({
  type: LOAD_BATCH_DIALER_INTEGRATION_ERROR,
  payload,
});

// Batchdialer
export const loadCampaignsIntegrationSuccess = (payload) => ({
  type: LOAD_CAMPAIGNS_INTEGRATION_SUCCESS,
  payload,
});

export const loadCampaignsIntegrationError = (payload) => ({
  type: LOAD_CAMPAIGNS_INTEGRATION_ERROR,
  payload,
});

export const loadStateareaCodeIntegrationSuccess = (payload) => ({
  type: LOAD_STATE_AREA_INTEGRATION_SUCCESS,
  payload,
});

export const loadStateareaCodeIntegrationError = (payload) => ({
  type: LOAD_STATE_AREA_INTEGRATION_ERROR,
  payload,
});

export const setBatchDialerSource = (payload) => ({
  type: SET_BATCH_DIALER_SOURCE,
  payload,
});

export const loadCallSettingIntegrationSuccess = (payload) => ({
  type: LOAD_CALL_SETTING_INTEGRATION_SUCCESS,
  payload,
});

export const loadCallSettingIntegrationError = (payload) => ({
  type: LOAD_CALL_SETTING_INTEGRATION_ERROR,
  payload,
});

export const deleteCallSettingSuccess = (payload) => ({
  type: DELETE_CALL_SETTING_SUCCESS,
  payload,
});

export const deleteCallSettingError = (payload) => ({
  type: DELETE_CALL_SETTING_ERROR,
  payload,
});

export const loadCallSettingsStateAreaCodesSuccess = (payload) => ({
  type: LOAD_CALL_SETTINGS_STATE_AREA_CODES_SUCCESS,
  payload,
});

export const loadCallSettingsStateAreaCodesError = (payload) => ({
  type: LOAD_CALL_SETTINGS_STATE_AREA_CODES_ERROR,
  payload,
});

export const loadingCallSettingsAvailableOutboundNumbers = (payload) => ({
  type: LOADING_CALL_SETTINGS_AVAILABLE_OUTBOUND_NUMBERS,
  payload,
});

export const loadCallSettingsAvailableOutboundNumbersSuccess = (payload) => ({
  type: LOAD_CALL_SETTINGS_AVAILABLE_OUTBOUND_NUMBERS_SUCCESS,
  payload,
});

export const loadCallSettingsAvailableOutboundNumbersError = (payload) => ({
  type: LOAD_CALL_SETTINGS_AVAILABLE_OUTBOUND_NUMBERS_ERROR,
  payload,
});

// User profile
export const getUserProfile = (userID) => {
  return async (dispatch) => {
    try {
      const response = await apiService.get(`/ps/profile/${userID}`);
      dispatch(loadUserProfileSuccess(response.data));
    } catch (error) {
      dispatch(loadUserProfileError(error.message));
    }
  };
};

export const getMarketingProfile = (userId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    apiService.get(`/ps/profile/marketing/${userId}`)
      .then((response) => {
        dispatch(loadMarketingProfileSuccess(response.data));
        resolve(response.data);
      })
      .catch((error) => {
        dispatch(loadMarketingProfileError(error.message));
        reject(error);
      });
  });
};

// Usage
export const getUsageSectionAPI = (userID) => {
  return async (dispatch) => {
    try {
      const response = await apiService.get(`/ps/usage/${userID}`);
      dispatch(loadUsageSuccess(response.data));
    } catch (error) {
      dispatch(loadUsageError(error.message));
    }
  };
};
// credits
export const getPrePaidCreditsSectionAPI = (userID) => {
  return async (dispatch) => {
    try {
      const response = await apiService.get(`/ps/prepaidcredits?userId=${userID}`);
      dispatch(loadPrepaidCreditsSuccess(response.data));
    } catch (error) {
      dispatch(loadPrepaidCreditsSuccess(error.message));
    }
  };
};

export const increaseUsageAPI = async (payload) => {
  try {
    const response = await apiService.post('/ps/increase/limit', payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response.data };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};
export const updateSelectedLeadAutomator = (payload) => ({
  type: UPDATE_SELECTED_LEAD_AUTOMATOR,
  payload,
})

export const getLeadAutomatorAPI = (userID) => {
  return async (dispatch) => {
    try {
      dispatch({ type: IS_LEAD_AUTOMATOR_LOADING, payload: true });

      const response = await apiService.get(`/ps/listautomator/users?userId=${userID}`);
      dispatch(loadLeadAutomatorSuccess(response.data));
    } catch (error) {
      dispatch({ type: IS_LEAD_AUTOMATOR_LOADING, payload: false });

      console.error('Error:', error.message);
    }
  };
};
export const updateTrial14LeadAutomator = (payload) => ({
  type: UPDATE_TRIAL_14_LEAD_AUTOMATOR,
  payload,
})
export const updatePaymentProfile = (payload) => ({
  type: UPDATE_PAYMENT_PROFILE,
  payload,
})

export const updateSelectedManagePlan = (payload) => ({
  type: UPDATE_SELECTED_MANAGE_PLAN,
  payload,
})

export const updateSelectedChooseTierTooltip = (payload) => ({
  type: UPDATE_SELECTED_CHOOSE_TIER_TOOLTIP,
  payload,
})

export const updateSelectedPlanAnnual = (payload) => ({
  type: UPDATE_PLAN_MONTHLY_ANNUAL,
  payload,
})

export const getTeamMembers = (userID) => {
  return async (dispatch) => {
    dispatch(loadTeamMembersRequest());
    try {
      const response = await apiService.get(`/ps/team/members/${userID}`);
      // const data =await teamRes
      dispatch(loadTeamMembersSuccess(response?.data?.teamMembers));
    } catch (error) {
      dispatch(loadTeamMembersFailure(error?.message));
    }
  };
};

export const getPlanDetails = (userID) => {
  return async (dispatch) => {
    try {
      const response = await apiService.get(`/ps/plan/${userID}`);
      dispatch(loadPlanSuccess(response?.data));
    } catch (error) {
      dispatch(loadPlanFailure(error?.message));
    }
  };
};

export const getLeadAutomatorPlanDetails = (userID) => {
  return async (dispatch) => {
    try {
      const response = await apiService.get(`/lead-automator/plan-mrcs/user/${userID}`);
      dispatch(loadLAPlanSuccess(response.data));
    } catch (error) {
      dispatch(loadLAPlanFailure(error.message));
    }
  };
};

export const checkEmailAvailabilityAPI = async (email, userId) => {
  try {
    const encodedEmail = encodeURIComponent(email);
    const encodedUserId = encodeURIComponent(userId);

    const response = await apiService.get(
      `/ps/team/member/emailexists?email=${encodedEmail}&userId=${encodedUserId}`
    );

    if (response.status === 200) {
      return { success: true, data: response.data };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const createTeamMemberAPI = async (teamMemberData) => {
  try {
    const response = await apiService.post('/ps/teammember', teamMemberData);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response.data };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateTeamMember = async (teamMemberData) => {
  try {
    const response = await apiService.put('/ps/teammember', teamMemberData);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response.data };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Undo Changes
export const UndoPendingTeamChangesAPI = async (payload) => {
  try {
    const response = await apiService.put('/ps/team/member/undo', payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response.data };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// teamdowngrade
export const DowngradeMemberTeamAPI = async (payload) => {
  try {
    const response = await apiService.put('/ps/team/member/downgrade', payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response.data };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const upgradeFullAccessTeamMemberAPI = async (payload) => {
  try {
    const response = await apiService.put('/ps/team/member/upgrade', payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response.data };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const RemoveTeamMemberAPI = async (payload) => {
  try {
    const response = await apiService.delete('/ps/team/member/remove', payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response.data };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};
// Batch Dialer Section
export const getBatchDialerIntegration = (userID) => {
  return async (dispatch) => {
    try {
      const response = await apiService.get(`/ps/integrations`);
      dispatch(loadBatchDialerIntegrationSuccess(response.data));
    } catch (error) {
      dispatch(loadBatchDialerIntegrationError(error.message));
    }
  };
};

export const getCampaignsIntegration = (userID) => {
  return async (dispatch) => {
    try {
      const response = await apiService.get(`/ps/integrations/batch-dialer/campaigns`);
      dispatch(loadCampaignsIntegrationSuccess(response.data));
    } catch (error) {
      dispatch(loadCampaignsIntegrationError(error.message));
    }
  };
};

export const deleteBatchDialerIntegration = (id) => {
  return async (dispatch) => {
    try {
      const response = await apiService.delete(`/ps/integrations/${id}`);

      if (response.status === 200 || response.status === 204) {
        dispatch(getBatchDialerIntegration()); // refresh list
        return { success: true };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };
};

//Call settings

const CALL_SETTINGS_RETRY_DELAY_MS = 1000;

export const getCallSettingIntegration = (userID) => {
  return async (dispatch) => {
    dispatch({ type: LOAD_CALL_SETTING_INTEGRATION });
    try {
      const response = await apiService.get(`/ps/integrations/batch-dialer/call-settings`);
      console.log("ACTION: API response →", response.data);
      dispatch(loadCallSettingIntegrationSuccess(response.data));
    } catch (error) {
      // gateway drops pooled upstream connections intermittently — retry once on a fresh connection
      console.error(`ACTION: call settings request failed, retrying once in ${CALL_SETTINGS_RETRY_DELAY_MS}ms →`, error.message);

      try {
        await new Promise((resolve) => setTimeout(resolve, CALL_SETTINGS_RETRY_DELAY_MS));

        const retryResponse = await apiService.get(`/ps/integrations/batch-dialer/call-settings`);
        console.log("ACTION: call settings retry succeeded →", retryResponse.data);
        dispatch(loadCallSettingIntegrationSuccess(retryResponse.data));
      } catch (retryError) {
        console.error("ACTION: call settings retry failed, keeping last known settings →", retryError.message);
        dispatch(loadCallSettingIntegrationError(retryError.message));
      }
    }
  };
};

export const deleteCallSetting = (userID) => {
  return async (dispatch) => {
    try {
      const response = await apiService.delete("/ps/integrations/batch-dialer/call-settings");
      if (response.status === 200) {
        dispatch(deleteCallSettingSuccess());
        // Refresh call settings after successful deletion
        dispatch(getCallSettingIntegration(userID));
        return { success: true };
      }
      dispatch(deleteCallSettingError('Failed to delete call setting'));
      return { success: false, error: 'Failed to delete call setting' };
    } catch (error) {
      dispatch(deleteCallSettingError(error.message));
      return { success: false, error: error.message || 'Failed to delete call setting' };
    }
  };
};

export const getCallSettingsAvailableOutboundNumbers = (state, areaCode) => {
  return async (dispatch) => {
    try {
      dispatch(loadingCallSettingsAvailableOutboundNumbers());

      const response = await apiService.get(`/ps/integrations/batch-dialer/call-settings/outbound/available?state=${state}&areaCode=${areaCode}`);
      console.log("ACTION: API response →", response.data);

      dispatch(loadCallSettingsAvailableOutboundNumbersSuccess(response.data));
    } catch (error) {
      dispatch(loadCallSettingsAvailableOutboundNumbersError(error.message));
    }
  }
}

export const getStateandAreaIntegration = () => {
  return async (dispatch) => {
    try {
      const response = await apiService.get(`/ps/integrations/batch-dialer/call-settings/state-area-codes`);
      dispatch(loadStateareaCodeIntegrationSuccess(response.data));
    } catch (error) {
      dispatch(loadStateareaCodeIntegrationError(error.message));
    }
  };
};

export const updateCallForwarding = (callSettingId, forwardingNumber, userID) => {
  return async (dispatch) => {
    try {
      const response = await apiService.patch(`/ps/integrations/batch-dialer/call-settings`, {
        forwardingNumber: forwardingNumber
      });
      if (response.status === 200) {
        // Refresh call settings after successful update
        dispatch(getCallSettingIntegration(userID));
        return { success: true, data: response.data };
      }
      return { success: false, error: 'Failed to update call forwarding' };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to update call forwarding' };
    }
  };
};

export const replaceCallSettingsOutboundPhoneNumber = (userId, payload) => {
  return async (dispatch) => {
    try {
      const response = await apiService.post(`/ps/integrations/batch-dialer/call-settings/outbound/replace`, payload);
      console.log("ACTION: API response →", response.data);

      // Refresh call settings after successful phone number replacement
      dispatch(getCallSettingIntegration(userId));

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to replace phone numbers.' };
    }
  };
};

// Click to Dial

export const getCallCountForToday = () => {
  return async (dispatch) => {
    dispatch({ type: LOAD_CALL_COUNT_FOR_TODAY });

    try {
      const response = await apiService.get(
        `/ps/integrations/batch-dialer/call-settings/call-count-for-today`
      );

      dispatch({
        type: LOAD_CALL_COUNT_FOR_TODAY_SUCCESS,
        payload: response.data,
      });
    } catch (error) {
      dispatch({
        type: LOAD_CALL_COUNT_FOR_TODAY_ERROR,
        payload: error.message,
      });
    }
  };
};

export const openClickToDial = (payload) => {
  return async (dispatch) => {
    if (typeof payload === "boolean") {
      dispatch({
        type: payload ? OPEN_CLICK_TO_DIAL : CLOSE_CLICK_TO_DIAL,
      });
      return;
    }

    dispatch({
      type: OPEN_CLICK_TO_DIAL,
      payload,
    });
  }
};

export const clickToDialIsLoaded = (payload) => {
  return async (dispatch) => {
    dispatch({
      type: IS_CLICK_TO_DIAL_LOADED,
      payload
    });
  };
};

export function getClickToDialContactPropertyDetailsById(contactId) {
  const result = {};
  result[CALL_API] = {
    method: 'get',
    path: `/resource/auth/ps4/user/contacts/click-to-dial-data/${contactId}`,
    successType: LOAD_CLICK_TO_DIAL_CONTACT_PROPERTY_DETAILS_SUCCESS,
    errorType: LOAD_CLICK_TO_DIAL_CONTACT_PROPERTY_DETAILS_ERROR,
  };

  return result;
}

export const checkContactsPropertyAssociation = (contactIds) => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      dispatch({
        [CALL_API]: {
          method: 'post',
          path: '/resource/auth/ps4/user/contacts/property-association-check',
          body: JSON.stringify({ contactIds }),
          headers: {
            'Content-Type': 'application/json',
          },

          startType: CHECK_CONTACTS_PROPERTY_ASSOCIATION,
          successType: CHECK_CONTACTS_PROPERTY_ASSOCIATION_SUCCESS,
          errorType: CHECK_CONTACTS_PROPERTY_ASSOCIATION_ERROR,

          afterSuccess: (res) => {
            resolve(res?.response || res);
          },

          afterError: (err) => {
            reject(err);
          },
        },
      });
    });
  };
};

export const saveCallLog = (payload) => {
  return async (dispatch) => {
    try {
      dispatch({
        type: LOAD_CALL_COUNT_FOR_TODAY,
      });

      const response = await apiService.post("/ps/integrations/batch-dialer/call-settings/call-logs", payload);
      console.log("ACTION: API response →", response?.data);

      dispatch({
        type: LOAD_CALL_COUNT_FOR_TODAY_SUCCESS,
        payload: response?.data,
      });
    } catch (error) {
      dispatch({
        type: LOAD_CALL_COUNT_FOR_TODAY_ERROR,
        payload: error?.message,
      });
    }
  }
};

// Billing section

export const duplicateDialerCampaign = (dialerCampaignId) => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      dispatch({
        [CALL_API]: {
          method: 'post',
          path: '/resource/auth/ps4/user/marketing/dialer/campaigns/duplicate',
          body: JSON.stringify({ campaignId: dialerCampaignId }),
          headers: {
            'Content-Type': 'application/json',
          },

          startType: DUPLICATE_DIALER_CAMPAIGN,
          successType: DUPLICATE_DIALER_CAMPAIGN_SUCCESS,
          errorType: DUPLICATE_DIALER_CAMPAIGN_ERROR,

          afterSuccess: (res) => {
            resolve(res?.response || res);
          },

          afterError: (err) => {
            reject(err);
          },
        },
      });
    });
  };
};

export const updateDialerCampaign = (dialerCampaignId, name, contactIds) => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      const body = { name };
      if (contactIds) body.contactIds = contactIds;

      dispatch({
        [CALL_API]: {
          method: 'patch',
          path: `/resource/auth/ps4/user/marketing/dialer/campaigns/${dialerCampaignId}`,
          body: JSON.stringify(body),
          headers: {
            'Content-Type': 'application/json',
          },

          startType: UPDATE_DIALER_CAMPAIGN,
          successType: UPDATE_DIALER_CAMPAIGN_SUCCESS,
          errorType: UPDATE_DIALER_CAMPAIGN_ERROR,

          afterSuccess: (res) => {
            resolve(res?.response || res);
          },

          afterError: (err) => {
            reject(err);
          },
        },
      });
    });
  };
};


export const createDialerCampaign = ({ name, marketingCampaignId, contactIds, originatingListName }) => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      dispatch({
        [CALL_API]: {
          method: 'put',
          path: '/resource/auth/ps4/user/marketing/dialer/campaigns',
          body: JSON.stringify({
            name,
            marketingCampaignId,
            contactIds,
            originatingListName
          }),
          headers: {
            'Content-Type': 'application/json',
          },

          startType: CREATE_DIALER_CAMPAIGN,
          successType: CREATE_DIALER_CAMPAIGN_SUCCESS,
          errorType: CREATE_DIALER_CAMPAIGN_ERROR,

          afterSuccess: (res) => {
            resolve(res?.response || res);
          },

          afterError: (err) => {
            reject(err);
          },
        },
      });
    });
  };
};

export const openLeadAutomatorUpgrade = (isOpen) => ({
  type: OPEN_LEAD_AUTOMATOR_UPGRADE,
  payload: isOpen,
});

// Billing section
export const getBillingDashboardAPI = (userID) => {
  return async (dispatch) => {
    try {
      const response = await apiService.get(`/billing/details/${userID}`);
      // const data =await billingDashboardMock
      dispatch(loadBillingDashboardSuccess(response.data));
    } catch (error) {
      dispatch(loadBillingDashboardError(error.message));
    }
  };
};

export const loadWalletList = (userID) => {
  return async (dispatch) => {
    try {
      const response = await apiService.get(`/wallet/all/${userID}`);
      // const data =await walletListMock
      dispatch(loadWalletSuccess(response?.data?.wallets));
    } catch (error) {
      dispatch(loadWalletError(error?.message));
    }
  };
};

export const addWalletAPI = async (payload) => {
  try {
    const response = await apiService.post('/wallet/add', payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateWalletAPI = async (payload) => {
  try {
    const response = await apiService.put('/wallet/update', payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteWalletAPI = async (userID, paymentId) => {
  try {
    const response = await apiService.delete(`/wallet/delete/${userID}/${paymentId}`);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteWalletUpdateDefaultAPI = async (req) => {
  try {
    const response = await apiService.delete(`/wallet/delete`, req);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};



export const loadDefaultPaymentSettingList = (userID) => {
  return async (dispatch) => {
    try {
      const response = await apiService.get(`/wallet/all/defaults/${userID}`);
      // const data =await defaultPaymentSettingList
      dispatch(loadWalletDefaultSettingSuccess(response?.data?.wallets));
    } catch (error) {
      dispatch(loadWalletDefaultSettingError(error?.message));
    }
  };
};

export const updateDefaultPaymentSettingList = async (payload) => {
  try {
    const response = await apiService.put(`/wallet/all/defaults`, payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updatePrepaidCredits = async (payload) => {
  try {
    const response = await apiService.post(`/ps/prepaidcredits`, payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const loadBillingHistoryList = (userId) => {
  return async (dispatch) => {
    try {
      const response = await apiService.get(`/billing/history/${userId}`);
      // const data =await getBillingList
      dispatch(loadBillingHistorySuccess(response?.data?.history));
    } catch (error) {
      dispatch(loadBillingHistoryError(error.message));
    }
  };
};

export const billingHistoryPDFList = async (userID, tabNumber) => {
  try {
    const { data: blobData } = await apiService.get(`/billing/history/download/${userID}`, {
      tab: tabNumber
    }, 'blob');
    const blobUrl = URL.createObjectURL(blobData);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `billing-history-${userID}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);

    return { success: true };
  } catch (error) {
    console.error(`Update wallet failed:`, error.message);
    return { success: false, error: error.message };
  }
};


export const upgradeSubscribeLeadAutomator = async (payload) => {
  try {
    const response = await apiService.post('/lead-automator/subscribe', payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export const downgradeSubscribeLeadAutomator = async (payload) => {
  try {
    const response = await apiService.post('/lead-automator/downgrade', payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export const unSubscribeLeadAutomator = async (payload) => {
  try {
    const response = await apiService.delete('/lead-automator/unsubscribe', payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export const revertUnsubscriptionLeadAutomator = async (payload) => {
  try {
    const response = await apiService.post('/connect/revert/unsubscription', payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export const trialLeadAutomator = async (payload) => {
  try {
    const response = await apiService.post('/lead-automator/trial', payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export const canceltrialLeadAutomator = async (payload) => {
  try {
    const response = await apiService.post('/connect/cancel-trial', payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export const getPlansTypeAPI = (userID) => {
  return async (dispatch) => {
    try {
      const response = await apiService.get(`/ps/team/plan/${userID}`);
      dispatch(loadPlanTypeSuccess(response.data));
    } catch (error) {
      dispatch(loadPlanTypeError(error.message));
    }
  };
};
export const upgradeDowngradeManagePlan = async (url, payload) => {
  try {
    const response = await apiService.post(`/ps/plan/${url}`, payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export const fetchPlanDueAmount = async (payload) => {
  try {
    const response = await apiService.post(`/ps/plan/due`, payload);
    if (response.status === 200 || response.status === 201) {
      return { success: true, data: response?.data };
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export const setCallCompliance = (payload) => ({
  type: SET_CALL_COMPLIANCE,
  payload,
});